<template>
  <div class="tags-combobox-wrapper w-100">
    <v-combobox
      v-model="object.tags"
      :items="Array.from(tagsMap.keys()).sort()"
      multiple
      chips
      closable-chips
      variant="outlined"
      density="compact"
      hide-details
      class="tags-combobox"
      @update:model-value="(updatedTags) =>  emit('update:modelValue', updatedTags)"
    >
      <template #chip="{ item, props }">
        <v-chip
          v-bind="props"
          :color="getTagColor(item.value)"
          size="small"
          class="tag-chip"
        >
          {{ tagsMap.get(item.value) || item.value }}
        </v-chip>
      </template>

      <template #item="{ item, props: itemProps }">
        <v-list-item v-bind="itemProps" :title="tagsMap.get(item.raw)" max-width="470">
          <template #prepend>
            <v-checkbox-btn
              :model-value="object.tags.includes(item.raw)"
              readonly
            />
          </template>
        </v-list-item>
      </template>
    </v-combobox>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { DomainModelRole } from '@/constants/cardTypes'

const { object } = defineProps({
  object: {
    type: Object,
    required: true
  }
})
const emit = defineEmits(['update:modelValue'])

const store = useStore()

const workflow = computed(() => store.state.workflow.workflow)
const tagsMap = computed(() => {
  if (!workflow.value) {
    return new Map()
  }

  const tags = new Map()

  if (workflow.value.schemas && Array.isArray(workflow.value.schemas)) {
    workflow.value.schemas.forEach((entity) => {
      if (entity.id) {
        tags.set(`entity-${entity.id}`, `Entity: ${entity.name}`)
      }
    })
  }

  if (workflow.value.domainEvents && Array.isArray(workflow.value.domainEvents)) {
    workflow.value.domainEvents.forEach((event) => {
      if (event.cards && Array.isArray(event.cards)) {
        event.cards.forEach((card) => {
          if (card.id && card.cardType && sanitizeCardDescription(card.description)) {
            switch (card.cardType.domainModelRole) {
              case DomainModelRole.COMMAND:
                tags.set(`command-${card.id}`, `Command: ${sanitizeCardDescription(card.description)}`)
                break
              case DomainModelRole.READ_MODEL:
                tags.set(`readmodel-${card.id}`, `Read Model: ${sanitizeCardDescription(card.description)}`)
                break
              case DomainModelRole.GIVEN_WHEN_THEN:
                tags.set(`gwt-${card.id}`, `GWT: ${sanitizeCardDescription(card.description)}`)
                break
              default:
                break
            }
          }
        })
      }
    })
  }

  return tags
})

const getTagColor = (tag) => {
  if (!tag) {
    return 'grey'
  }
  const prefix = tag.split('-')[0]

  switch (prefix) {
    case 'entity':
      return 'amber-darken-1'
    case 'command':
      return 'blue-lighten-1'
    case 'readmodel':
      return 'green-lighten-1'
    case 'gwt':
      return 'purple-lighten-1'
    default:
      return 'grey'
  }
}
</script>

<style scoped>
.tags-combobox {
  width: 500px;
  max-width: 100%;
}

.tag-chip {
  margin: 2px;
}

:deep(.tags-combobox.v-combobox) {
  .v-field__input {
    min-height: 20px !important;
    padding-top: 5px !important;
    padding-bottom: 5px !important;
    padding-left: 5px !important;
  }

  .v-input__details {
    display: none !important;
  }

  .v-combobox__selection {
    overflow: hidden;
  }
}
</style>
